import { contactPipelineInput } from 'app/lib/contactPipelineInput'
import { Plus, Trash } from 'iconoir-react'
import { compact } from 'lodash'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import Spinner from '../Spinner'
import type { PipelineCatalogEntry } from 'app/components/action-center/usePipelinesCatalog'
import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import { AddPipeline } from 'app/components/contacts/AddPipeline'
import { PipelineStageDropdown } from 'app/components/contacts/PipelineStageDropdown'
import { ListMembershipRow } from 'app/components/contacts/ListMembershipRow'
import { RuleListRows } from 'app/components/contacts/RuleListRows'
import { Button } from 'app/components/ui/button'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { useListPipeline } from 'app/hooks/useListPipeline'
import { usePracticeListMemberships } from 'app/hooks/usePracticeListMemberships'
import { showModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { useContactStore, usePipelineStore } from 'app/stores/StoreContext'
import type { ContactJson, StageType } from 'app/types/entities'
import { useUser } from 'app/utils'
import { isCompanionPipeline, visibleMemberships } from 'lib/companionPipelines'

// The dynamic `border-pipelines-*` classes are generated centrally via the
// `@source inline(...)` directive in src/styles/tailwind.css — no per-file safelist needed.

type PipelineEntry = { id: string; stage?: string }

interface ContactCardPipelinesProps {
  contact: ContactJson
}

/**
 * Compact, editable pipelines block for a contact card in the inbox right panel.
 * Mirrors the ContactPipelinesV2 look (colored stage border, name · stage) but
 * adds inline stage-change and removal, plus the shared "Add to pipeline" modal.
 */
export const ContactCardPipelines = observer(function ContactCardPipelines({
  contact,
}: ContactCardPipelinesProps): ReactElement | null {
  const user = useUser()
  const contactStore = useContactStore()
  const pipelineStore = usePipelineStore()
  const copy = useListCopy()
  const groupedNav = useFeatureToggle(`grouped-navigation`)
  const practice = usePracticeListMemberships(contact, { enabled: !!groupedNav })
  // Optimistic override so edits reflect instantly; cleared when the prop catches up.
  const [optimistic, setOptimistic] = useState<PipelineEntry[] | undefined>()

  const pipelines = useMemo<PipelineEntry[]>(
    () => compact(optimistic ?? contact.pipelines ?? []) as PipelineEntry[],
    [optimistic, contact.pipelines],
  )

  useEffect(() => setOptimistic(undefined), [contact.pipelines])

  useEffect(() => {
    if (!pipelineStore.list && !pipelineStore.listLoading) pipelineStore.fetchList()
  }, [pipelineStore])

  if (!user?.workspaceId) return null

  // With the grouped nav on, a system membership is drawn by its rule row alone. Only what is
  // drawn is counted; a write still sends `pipelines` whole.
  const shown = visibleMemberships(pipelines, pipelineStore.hits, { hideSystem: !!groupedNav })
  const systemPipelines = pipelines.filter((p) => isSystemPipelineId(p.id))
  const practiceCards = practice.entries.flatMap((entry) => {
    const catalogEntry = practice.catalog.find((list) => list.id === entry.id)
    return catalogEntry ? [{ entry, catalogEntry }] : []
  })
  const hasLists = groupedNav ? practice.entries.length > 0 : shown.length > 0

  const persist = async (next: PipelineEntry[]) => {
    const input = contactPipelineInput(next)
    if (!input) {
      notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
      return
    }

    // Optimistic echo so the card reflects the change instantly; cleared on error, and
    // overwritten by the interned shared instance once `contactStore.update` resolves.
    setOptimistic(next)
    const result = await contactStore.update(contact.id!, {
      pipelines: input,
    })
    if (result.error) {
      setOptimistic(undefined)
      notifyError(copy.failedToUpdateList, `pipeline-update-error`)
    }
  }

  const changeStage = (pipelineId: string, stage: string) =>
    persist(
      pipelines.some((p) => p.id === pipelineId)
        ? pipelines.map((p) => (p.id === pipelineId ? { ...p, stage } : p))
        : [...pipelines, { id: pipelineId, stage }],
    )

  const removePipeline = (pipelineId: string) => persist(pipelines.filter((p) => p.id !== pipelineId))

  const openAddModal = () => {
    showModal(
      <AddPipeline
        contact={{ ...contact, pipelines } as ContactJson}
        onContactChange={(updated) => {
          setOptimistic(compact(updated.pipelines ?? []) as PipelineEntry[])
        }}
        lists={groupedNav ? practice.addable : undefined}
        onAddToList={groupedNav ? practice.add : undefined}
      />,
      { title: copy.addToList, isFullScreen: false, removePadding: true, overflowHidden: false, useRadix: true },
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      {groupedNav && <RuleListRows contact={{ ...contact, pipelines: systemPipelines } as ContactJson} />}
      {groupedNav
        ? practiceCards.map(({ entry, catalogEntry }) => (
            <EditablePipelineCard
              key={entry.id}
              pipelineId={entry.id}
              stageName={entry.stage ?? ``}
              entry={catalogEntry}
              onChangeStage={(stage) => practice.changeStage(entry.id, stage)}
              onRemove={practice.canRemove(entry.id) ? () => practice.remove(entry.id) : undefined}
            />
          ))
        : shown.map((p, index) => (
            <EditablePipelineCard
              key={`${p.id}-${index}`}
              pipelineId={p.id}
              stageName={p.stage ?? ``}
              onChangeStage={(stage) => changeStage(p.id, stage)}
              onRemove={() => removePipeline(p.id)}
            />
          ))}
      {hasLists ? (
        <Button variant="subtle" size="xs" onClick={openAddModal} className="self-start">
          <Plus className="size-3.5" strokeWidth={2} />
          {copy.addToList}
        </Button>
      ) : (
        // No pipelines yet: keep the affordance light so it doesn't add visual
        // weight to every inbox card. Fills out into the full list once populated.
        <Button variant="subtle" size="inline" onClick={openAddModal} className="self-start">
          <Plus className="size-3" strokeWidth={2} />
          <span className="text-label-xs">{copy.addToAList}</span>
        </Button>
      )}
    </div>
  )
})

interface EditablePipelineCardProps {
  pipelineId: string
  stageName: string
  onChangeStage: (stage: string) => void
  onRemove?: () => void
  /** A practice list from the List API: drawn as given, with no pipeline fetch. */
  entry?: PipelineCatalogEntry
}

function EditablePipelineCard({
  pipelineId,
  stageName,
  onChangeStage,
  onRemove,
  entry,
}: EditablePipelineCardProps): ReactElement {
  const copy = useListCopy()
  const { pipeline, error } = useListPipeline(pipelineId, !!entry)

  const sourceStages = entry?.stages ?? pipeline?.stages
  const stages = useMemo<StageType[]>(
    () => [...(sourceStages ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as StageType[],
    [sourceStages],
  )
  const stage = useMemo(() => stages.find((s) => s.name === stageName), [stages, stageName])
  const isLoading = !entry && !pipeline && !error

  if (error) {
    return (
      <div className="rounded-lg border border-default bg-surface-subtle p-2.5 text-label-xs text-red-600">
        {copy.errorLoadingList}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-default bg-surface-subtle p-2.5">
        <Spinner size={4} />
      </div>
    )
  }

  // The block filters companions out before it draws a card; this covers the moment before
  // the pipeline list has loaded, when the block cannot yet tell a companion apart.
  if (!entry && (!pipeline || isCompanionPipeline(pipeline))) return <div />

  const name = entry?.name ?? pipeline?.name ?? ``

  return (
    <ListMembershipRow
      id={pipelineId}
      name={name}
      stage={<PipelineStageDropdown stages={stages} currentStage={stage?.name ?? ``} onChange={onChangeStage} />}
      actions={
        onRemove && (
          <Button variant="subtle" size="inline" aria-label={copy.removeFromList} onClick={onRemove} className="size-5">
            <Trash className="size-3.5" strokeWidth={2} />
          </Button>
        )
      }
    />
  )
}
